import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import './UserCreate.css'
import { MainLayout } from '../../Layout/MainLayout'

interface Branch {
    id: string
    name: string
    code: string
}

const POSITIONS = [
    { value: 'vendedor', label: 'Vendedor' },
    { value: 'gerente_loja', label: 'Gerente de Loja' },
    { value: 'gerente_regional', label: 'Gerente Regional' },
    { value: 'gerente_nacional', label: 'Gerente Nacional' },
    { value: 'rh', label: 'RH' },
    { value: 'ti', label: 'TI' },
    { value: 'admin', label: 'Admin' },
]

export function CreateUser() {
    const navigate = useNavigate()
    const [branches, setBranches] = useState<Branch[]>([])
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState('')

    const [form, setForm] = useState({
        name: '',
        cpf: '',
        email: '',
        phone: '',
        password: '',
        branchId: '',
        position: '',
    })

    useEffect(() => {
        api.get('/branch').then(r => setBranches(r.data?.data ?? r.data)).catch(() => { })
    }, [])

    function set(field: string, value: string) {
        setForm(prev => ({ ...prev, [field]: value }))
        setError('')
    }

    function formatCpf(value: string) {
        const digits = value.replace(/\D/g, '').slice(0, 11)
        return digits
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }

    function formatPhone(value: string) {
        const digits = value.replace(/\D/g, '').slice(0, 11)
        return digits
            .replace(/(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{5})(\d{1,4})$/, '$1-$2')
    }

    function validate() {
        if (!form.name.trim()) return 'Nome é obrigatório'
        if (!form.cpf.trim()) return 'CPF é obrigatório'
        if (!form.password.trim()) return 'Senha é obrigatória'
        if (!form.position) return 'Cargo é obrigatório'
        return null
    }

    async function handleSubmit() {
        const err = validate()
        if (err) { setError(err); return }

        setLoading(true)
        setError('')
        try {
            await api.post('/user', {
                name: form.name,
                cpf: form.cpf.replace(/\D/g, ''),
                email: form.email || undefined,
                phone: form.phone || undefined,
                password: form.password,
                branchId: form.branchId || undefined,
                position: form.position,
            })
            setSuccess(true)
            setTimeout(() => navigate('/admin'), 1800)
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erro ao criar usuário')
        } finally {
            setLoading(false)
        }
    }

    if (success) return (
        <MainLayout>
            <div className="cu-success-wrap">
                <div className="cu-success-icon">✓</div>
                <h2 className="serif">Usuário criado!</h2>
                <p>Redirecionando para o painel...</p>
            </div>
        </MainLayout>
    )

    return (
        <MainLayout>
            <div className="container fade-in">
                <div className="breadcrumb">
                    <a onClick={() => navigate('/admin')}>Admin</a>
                    <span className="sep">›</span>
                    <span>Novo usuário</span>
                </div>

                <div className="cu-layout">
                    <div className="cu-header">
                        <div className="kicker">Gestão de pessoas</div>
                        <h1 className="serif">Novo usuário</h1>
                        <p>Preencha os dados do colaborador. CPF e senha são obrigatórios para o primeiro acesso.</p>
                    </div>

                    <div className="cu-form-card">
                        {error && <div className="cu-error">{error}</div>}

                        <div className="cu-section-label">Dados pessoais</div>
                        <div className="cu-row">
                            <div className="field" style={{ flex: 2 }}>
                                <label>Nome completo *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => set('name', e.target.value)}
                                    placeholder="Nome do colaborador"
                                    autoFocus
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label>CPF *</label>
                                <input
                                    type="text"
                                    value={form.cpf}
                                    onChange={e => set('cpf', formatCpf(e.target.value))}
                                    placeholder="000.000.000-00"
                                />
                            </div>
                        </div>

                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>E-mail</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={e => set('email', e.target.value)}
                                    placeholder="email@gazin.com.br"
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label>Telefone</label>
                                <input
                                    type="text"
                                    value={form.phone}
                                    onChange={e => set('phone', formatPhone(e.target.value))}
                                    placeholder="(00) 00000-0000"
                                />
                            </div>
                        </div>

                        <div className="cu-divider" />
                        <div className="cu-section-label">Acesso e cargo</div>

                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>Senha provisória *</label>
                                <input
                                    type="password"
                                    value={form.password}
                                    onChange={e => set('password', e.target.value)}
                                    placeholder="Mínimo 8 caracteres"
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label>Cargo *</label>
                                <select
                                    value={form.position}
                                    onChange={e => set('position', e.target.value)}
                                    className="cu-select"
                                >
                                    <option value="">Selecione o cargo</option>
                                    {POSITIONS.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="field">
                            <label>Filial</label>
                            <select
                                value={form.branchId}
                                onChange={e => set('branchId', e.target.value)}
                                className="cu-select"
                            >
                                <option value="">Sem filial (matriz / RH / TI)</option>
                                {branches.map(b => (
                                    <option key={b.id} value={b.id}>
                                        {b.code} · {b.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="cu-divider" />

                        <div className="cu-actions">
                            <button
                                className="btn btn-ghost"
                                onClick={() => navigate('/admin')}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Criando...' : 'Criar usuário →'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    )
}